import React, { useState, useRef, useEffect } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { X, Upload, Trash2, CheckCircle, Circle, Camera } from 'lucide-react';
import './AddPermitModal.css';

const AddPermitModal = ({ onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState(null);
  const [formData, setFormData] = useState({
    permit_number: '',
    permit_type: 'firearms',
    issue_date: '',
    expiration_date: '',
    custom_permit_name: '',
    employee_name: '',
    last_qualification_date: '',
  });
  const [permitImage, setPermitImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const fileInputRef = useRef(null);
  
  // Firearms qualification tracking
  const [qualifications, setQualifications] = useState({
    qual_1: { date: '', completed: false, photo: null },
    qual_2: { date: '', completed: false, photo: null },
    qual_3: { date: '', completed: false, photo: null },
    qual_4_renewal: { date: '', completed: false, photo: null },
  });
  const qualPhotoRefs = {
    qual_1: useRef(null),
    qual_2: useRef(null),
    qual_3: useRef(null),
    qual_4_renewal: useRef(null),
  };
  
  // Guard card training tracking
  const [training32hrsCompleted, setTraining32hrsCompleted] = useState(null);

  useEffect(() => {
    // Fetch user to check account type
    const fetchUser = async () => {
      try {
        const response = await axiosInstance.get('/auth/me');
        setUser(response.data);
      } catch (error) {
        console.error('Failed to fetch user');
      }
    };
    fetchUser();
  }, []);

  const permitTypes = [
    // BSIS Permits
    { value: 'firearms', label: 'BSIS Exposed Firearm Permit', category: 'BSIS' },
    { value: 'guard_card', label: 'BSIS Guard Card', category: 'BSIS' },
    { value: 'baton', label: 'BSIS Baton Permit', category: 'BSIS' },
    { value: 'bsis_ppo', label: 'BSIS PPO License', category: 'BSIS' },
    { value: 'bsis_qm', label: 'BSIS QM License', category: 'BSIS' },
    { value: 'bsis_pi', label: 'BSIS Private Investigator License', category: 'BSIS' },
    { value: 'bsis_locksmith', label: 'BSIS Locksmith License', category: 'BSIS' },
    { value: 'bsis_alarm', label: 'BSIS Alarm Company License', category: 'BSIS' },
    
    // CCW Permits
    { value: 'ccw_ca', label: 'California CCW Permit', category: 'CCW' },
    { value: 'ccw_out_of_state', label: 'Out of State CCW Permit', category: 'CCW' },
    { value: 'hr218', label: 'HR218 LEOSA Permit', category: 'CCW' },
    
    // Professional Permits
    { value: 'doj_coe', label: 'DOJ COE License (Renews Yearly)', category: 'Professional' },
    { value: 'doj_firearms_instructor', label: 'CA DOJ Firearms Instructor License', category: 'Professional' },
    { value: 'doj_ccw_instructor', label: 'CA DOJ CCW Instructor License', category: 'Professional' },
    { value: 'first_aid', label: 'First Aid License', category: 'Professional' },
    { value: 'cpr', label: 'CPR License', category: 'Professional' },
    { value: 'tecc', label: 'TECC License', category: 'Professional' },
    { value: 'axon_taser_instructor', label: 'AXON TASER Instructor License', category: 'Professional' },
    { value: 'sabre_oc_instructor', label: 'SABRE OC Instructor License', category: 'Professional' },
    { value: 'nra_instructor', label: 'NRA Instructor License', category: 'Professional' },
    { value: 'uscca_instructor', label: 'USCCA Instructor License', category: 'Professional' },
    { value: 'crpa_instructor', label: 'CRPA Instructor License', category: 'Professional' },
    
    // Personal Permits
    { value: 'vehicle_insurance', label: 'Vehicle Insurance', category: 'Personal' },
    
    // Custom option
    { value: 'other', label: 'OTHER (Custom Permit or License)', category: 'Custom' },
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image size must be less than 5MB');
        return;
      }
      if (!file.type.startsWith('image/')) {
        toast.error('Please upload an image file');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setPermitImage(reader.result);
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setPermitImage(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Handle qualification date/completed changes
  const handleQualChange = (qualKey, field, value) => {
    setQualifications(prev => ({
      ...prev,
      [qualKey]: {
        ...prev[qualKey],
        [field]: value
      }
    }));
  };

  // Handle qualification photo upload
  const handleQualPhotoUpload = (qualKey, e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image size must be less than 5MB');
        return;
      }
      if (!file.type.startsWith('image/')) {
        toast.error('Please upload an image file');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        handleQualChange(qualKey, 'photo', reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveQualPhoto = (qualKey) => {
    handleQualChange(qualKey, 'photo', null);
    if (qualPhotoRefs[qualKey].current) {
      qualPhotoRefs[qualKey].current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Validate custom permit name if "other" is selected
    if (formData.permit_type === 'other' && !formData.custom_permit_name.trim()) {
      toast.error('Please enter a permit type name');
      setLoading(false);
      return;
    }

    try {
      // Helper function to safely parse date string to ISO format
      const parseDate = (dateStr) => {
        if (!dateStr) return null;
        // If already in ISO format with time, return as is
        if (dateStr.includes('T')) return dateStr;
        // For YYYY-MM-DD format, append time to avoid timezone issues
        const [year, month, day] = dateStr.split('-');
        // Ensure year is 4 digits
        const fullYear = year.length === 4 ? year : year.padStart(4, '20');
        return `${fullYear}-${month}-${day}T12:00:00.000Z`;
      };

      const submitData = {
        permit_number: formData.permit_number,
        permit_type: formData.permit_type,
        issue_date: parseDate(formData.issue_date),
        expiration_date: parseDate(formData.expiration_date),
        custom_permit_name: formData.permit_type === 'other' ? formData.custom_permit_name.trim() : null,
        permit_image: permitImage,
        employee_name: user?.account_type === 'company' ? formData.employee_name.trim() || null : null,
        last_qualification_date: formData.permit_type === 'firearms' && formData.last_qualification_date 
          ? parseDate(formData.last_qualification_date) 
          : null,
      };

      // Add firearms qualification data
      if (formData.permit_type === 'firearms') {
        submitData.qual_1 = {
          date: qualifications.qual_1.date ? parseDate(qualifications.qual_1.date) : null,
          completed: qualifications.qual_1.completed,
          photo: qualifications.qual_1.photo
        };
        submitData.qual_2 = {
          date: qualifications.qual_2.date ? parseDate(qualifications.qual_2.date) : null,
          completed: qualifications.qual_2.completed,
          photo: qualifications.qual_2.photo
        };
        submitData.qual_3 = {
          date: qualifications.qual_3.date ? parseDate(qualifications.qual_3.date) : null,
          completed: qualifications.qual_3.completed,
          photo: qualifications.qual_3.photo
        };
        submitData.qual_4_renewal = {
          date: qualifications.qual_4_renewal.date ? parseDate(qualifications.qual_4_renewal.date) : null,
          completed: qualifications.qual_4_renewal.completed,
          photo: qualifications.qual_4_renewal.photo
        };
      }

      // Add guard card specific fields
      if (formData.permit_type === 'guard_card') {
        submitData.training_32hrs_completed = training32hrsCompleted;
      }

      await axiosInstance.post('/permits', submitData);
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add permit');
    } finally {
      setLoading(false);
    }
  };

  const selectedPermit = permitTypes.find((p) => p.value === formData.permit_type);

  // Group permits by category for the dropdown
  const groupedPermits = permitTypes.reduce((acc, permit) => {
    if (!acc[permit.category]) {
      acc[permit.category] = [];
    }
    acc[permit.category].push(permit);
    return acc;
  }, {});

  // Render qualification field
  const renderQualificationField = (qualKey, label) => (
    <div key={qualKey} className="qualification-field">
      <div className="qual-header">
        <span className="qual-label">{label}</span>
        <button
          type="button"
          className={`qual-complete-btn ${qualifications[qualKey].completed ? 'completed' : ''}`}
          onClick={() => handleQualChange(qualKey, 'completed', !qualifications[qualKey].completed)}
        >
          {qualifications[qualKey].completed ? (
            <><CheckCircle size={18} /> Completed</>
          ) : (
            <><Circle size={18} /> Mark Complete</>
          )}
        </button>
      </div>
      <div className="qual-inputs">
        <div className="qual-date-input">
          <label>Date Completed</label>
          <input
            type="date"
            className="input-field"
            value={qualifications[qualKey].date}
            onChange={(e) => handleQualChange(qualKey, 'date', e.target.value)}
          />
        </div>
        <div className="qual-photo-input">
          <label>Qualification Form Photo</label>
          {qualifications[qualKey].photo ? (
            <div className="qual-photo-preview">
              <img src={qualifications[qualKey].photo} alt={`${label} form`} />
              <button
                type="button"
                className="remove-qual-photo-btn"
                onClick={() => handleRemoveQualPhoto(qualKey)}
              >
                <Trash2 size={14} /> Remove
              </button>
            </div>
          ) : (
            <div
              className="qual-photo-upload-box"
              onClick={() => qualPhotoRefs[qualKey].current?.click()}
            >
              <Camera size={18} />
              <span>Add Photo</span>
            </div>
          )}
          <input
            ref={qualPhotoRefs[qualKey]}
            type="file"
            accept="image/*"
            onChange={(e) => handleQualPhotoUpload(qualKey, e)}
            style={{ display: 'none' }}
          />
        </div>
      </div>
    </div>
  );

  return (
    <div className="modal-overlay" data-testid="add-permit-modal">
      <div className="modal-backdrop" onClick={onClose}></div>
      <div className="modal-content modal-content-large">
        <div className="modal-header">
          <h2 data-testid="modal-title">Add New Permit</h2>
          <button
            className="modal-close-btn"
            onClick={onClose}
            data-testid="modal-close-btn"
          >
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="permit-form" data-testid="permit-form">
          <div className="form-group">
            <label className="label">Permit Type</label>
            <select
              name="permit_type"
              className="input-field"
              value={formData.permit_type}
              onChange={handleInputChange}
              required
              data-testid="permit-type-select"
            >
              {Object.entries(groupedPermits).map(([category, permits]) => (
                <optgroup key={category} label={`── ${category} ──`}>
                  {permits.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>

          {/* Custom Permit Name Field - Only shown when "OTHER" is selected */}
          {formData.permit_type === 'other' && (
            <div className="form-group custom-permit-section">
              <label className="label">Permit Type Name *</label>
              <input
                type="text"
                name="custom_permit_name"
                className="input-field"
                value={formData.custom_permit_name}
                onChange={handleInputChange}
                placeholder="Enter the name of your permit (e.g., HR218 LEOSA)"
                required
                data-testid="custom-permit-name-input"
              />
              <p className="field-hint">Enter the specific name of your permit or license</p>
            </div>
          )}

          {/* Employee Name Field - Only shown for Company accounts */}
          {user?.account_type === 'company' && (
            <div className="form-group employee-section">
              <label className="label">Employee Name</label>
              <input
                type="text"
                name="employee_name"
                className="input-field"
                value={formData.employee_name}
                onChange={handleInputChange}
                placeholder="Enter employee's full name"
                data-testid="employee-name-input"
              />
              <p className="field-hint">Name of the employee this permit belongs to</p>
            </div>
          )}

          <div className="form-group">
            <label className="label">Permit Number</label>
            <input
              type="text"
              name="permit_number"
              className="input-field"
              value={formData.permit_number}
              onChange={handleInputChange}
              placeholder="e.g., EXP-12345"
              required
              data-testid="permit-number-input"
            />
          </div>

          <div className="form-group">
            <label className="label">Issue Date</label>
            <input
              type="date"
              name="issue_date"
              className="input-field"
              value={formData.issue_date}
              onChange={handleInputChange}
              required
              data-testid="issue-date-input"
            />
          </div>

          <div className="form-group">
            <label className="label">Expiration Date</label>
            <input
              type="date"
              name="expiration_date"
              className="input-field"
              value={formData.expiration_date}
              onChange={handleInputChange}
              required
              data-testid="expiration-date-input"
            />
            <p className="field-hint">Enter the expiration date from the permit</p>
          </div>

          {/* GUARD CARD - 32 Hours Training Section */}
          {formData.permit_type === 'guard_card' && (
            <div className="form-group guard-card-training-section">
              <label className="label">Have You Completed Your 32 HRS Additional Training?</label>
              <div className="training-checkbox-group">
                <label className={`training-checkbox ${training32hrsCompleted === true ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="training_32hrs"
                    checked={training32hrsCompleted === true}
                    onChange={() => setTraining32hrsCompleted(true)}
                  />
                  <CheckCircle size={20} />
                  <span>YES</span>
                </label>
                <label className={`training-checkbox ${training32hrsCompleted === false ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="training_32hrs"
                    checked={training32hrsCompleted === false}
                    onChange={() => setTraining32hrsCompleted(false)}
                  />
                  <Circle size={20} />
                  <span>NO</span>
                </label>
              </div>
            </div>
          )}

          {/* FIREARMS - Qualification Tracking Section */}
          {formData.permit_type === 'firearms' && (
            <div className="form-group firearms-qualification-section">
              <label className="label section-label">Qualification Tracking</label>
              <p className="field-hint">Track your 4 required qualifications (4-6 month intervals)</p>
              <div className="qualifications-grid">
                {renderQualificationField('qual_1', 'BSIS EXPOSED FIREARM QUALIFICATION 1')}
                {renderQualificationField('qual_2', 'BSIS EXPOSED FIREARM QUALIFICATION 2')}
                {renderQualificationField('qual_3', 'BSIS EXPOSED FIREARM QUALIFICATION 3')}
                {renderQualificationField('qual_4_renewal', 'BSIS EXPOSED FIREARM QUALIFICATION 4 (Renewal)')}
              </div>
            </div>
          )}

          {/* Optional Photo Upload */}
          <div className="form-group">
            <label className="label">Permit Photo (Optional)</label>
            <div className="photo-upload-section">
              {imagePreview ? (
                <div className="image-preview-container">
                  <img src={imagePreview} alt="Permit preview" className="image-preview" />
                  <button
                    type="button"
                    className="remove-image-btn"
                    onClick={handleRemoveImage}
                    data-testid="remove-image-btn"
                  >
                    <Trash2 size={16} />
                    Remove
                  </button>
                </div>
              ) : (
                <div
                  className="photo-upload-box"
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="photo-upload-box"
                >
                  <Upload size={24} />
                  <span>Click to upload permit photo</span>
                  <span className="upload-hint">JPG, PNG up to 5MB</span>
                </div>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                style={{ display: 'none' }}
                data-testid="photo-input"
              />
            </div>
          </div>

          <div className="info-box">
            {formData.permit_type === 'firearms' ? (
              <p>
                <strong>Firearms Permit:</strong> Qualifications must be completed every 4-6 months. 
                You&apos;ll receive reminders before each qualification window.
              </p>
            ) : formData.permit_type === 'guard_card' ? (
              <p>
                <strong>Guard Card:</strong> You must complete 32 hours of additional training. 
                You&apos;ll receive a reminder 90 days before your permit expires.
              </p>
            ) : formData.permit_type === 'other' ? (
              <p>
                <strong>Custom Permit:</strong> You&apos;ll receive a reminder 90 days before 
                your permit expires. Track any license, certification, or permit.
              </p>
            ) : (
              <p>
                <strong>{selectedPermit?.label}:</strong> You&apos;ll receive a reminder 90 days
                before your permit expires.
              </p>
            )}
          </div>

          <div className="modal-actions">
            <button
              type="button"
              className="btn-outline"
              onClick={onClose}
              data-testid="cancel-btn"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={loading}
              data-testid="submit-btn"
            >
              {loading ? 'Adding...' : 'Add Permit'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddPermitModal;
